/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DockerHost, CreateDockerHostRequest } from "@/api/docker";

interface DockerHostFormProps {
  host?: DockerHost | null;
  onSubmit: (data: CreateDockerHostRequest) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  isOpen: boolean;
  defaultPollIntervalSeconds: number;
}

const emptyForm = (defaultInterval: number): CreateDockerHostRequest => ({
  name: "",
  host: "unix:///var/run/docker.sock",
  pollIntervalSeconds: defaultInterval,
  enabled: true,
});

export const DockerHostForm: React.FC<DockerHostFormProps> = ({
  host,
  onSubmit,
  onCancel,
  isSubmitting,
  isOpen,
  defaultPollIntervalSeconds,
}) => {
  const [formData, setFormData] = useState<CreateDockerHostRequest>(
    emptyForm(defaultPollIntervalSeconds),
  );

  useEffect(() => {
    if (host) {
      setFormData({
        name: host.name,
        host: host.host,
        pollIntervalSeconds: host.pollIntervalSeconds,
        enabled: host.enabled,
      });
    } else if (isOpen) {
      setFormData(emptyForm(defaultPollIntervalSeconds));
    }
  }, [host, isOpen, defaultPollIntervalSeconds]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ ...formData, name: formData.name.trim(), host: formData.host.trim() });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onCancel}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{host ? "Edit Docker Host" : "Add Docker Host"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="docker-name">Name</Label>
            <Input
              id="docker-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Home Server"
              required
            />
          </div>

          <div>
            <Label htmlFor="docker-host">Docker Host</Label>
            <Input
              id="docker-host"
              value={formData.host}
              onChange={(e) => setFormData({ ...formData, host: e.target.value })}
              placeholder="unix:///var/run/docker.sock"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Local socket (default) or a remote daemon, e.g. tcp://host:2375
            </p>
          </div>

          <div>
            <Label htmlFor="docker-interval">Poll Interval (s)</Label>
            <Input
              id="docker-interval"
              type="number"
              min={5}
              value={formData.pollIntervalSeconds}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  pollIntervalSeconds: Number(e.target.value) || defaultPollIntervalSeconds,
                })
              }
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="docker-enabled"
              checked={formData.enabled}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, enabled: checked as boolean })
              }
            />
            <Label htmlFor="docker-enabled" className="cursor-pointer">
              Enable polling
            </Label>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={isSubmitting} isLoading={isSubmitting}>
              {host ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
